import PropTypes from "prop-types";

/**
 * RealisticSolarPanel – High-fidelity 3D photovoltaic solar panel component.
 * Features:
 * - Dynamic Sun in the sky above the solar panel: Rises higher and radiates brightly at high generation power; dims and sinks when power drops
 * - Active Electricity Current Flow between solar panel busbars and grid lines
 * - Electricity flow speed and intensity scales dynamically with live generation value (kW)
 * - Animated sunlight sheen sweep across silicon solar cells
 * - Support base status LED
 */
const RealisticSolarPanel = ({
  liveValue,
  isRunning,
  isOffline,
  statusColor = "grey",
  speedScale = 1,
  ratio = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

  const powerRatio = isRunning && !isOffline ? Math.min(1, Math.max(0.08, typeof ratio === "number" ? ratio : 1)) : 0;

  // Sun position in the sky ABOVE the top panel frame (y = 4 is panel top):
  // High power: sun rises to y = -46 and shines brightly
  // Low power: sun sinks to y = -26 and dims
  const sunY = -26 - powerRatio * 20;
  const sunRadius = 14 + powerRatio * 5;
  const sunOpacity = isRunning && !isOffline ? 0.35 + powerRatio * 0.65 : 0;

  // Electricity pulse duration (faster when power is higher)
  const currentDuration = isRunning && !isOffline ? `${Math.max(0.35, 1.4 / (speedScale || 1))}s` : "0s";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Image */}
      <img
        src="/scada-realistic/solar-panel.png"
        alt="SCADA Realistic Solar Panel"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 573x276 space, overflow visible for sky sun) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 573 276"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ overflow: "visible" }}
      >
        <defs>
          <clipPath id="solarCellsClip">
            <polygon points="68,4 518,4 565,214 20,214" />
          </clipPath>

          <radialGradient id="sunFlareGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="30%" stopColor="#fef08a" stopOpacity="0.95" />
            <stop offset="70%" stopColor="#f59e0b" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#ea580c" stopOpacity="0" />
          </radialGradient>

          <linearGradient id="solarSheenGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="45%" stopColor="#ffffff" stopOpacity={0.25 * powerRatio} />
            <stop offset="50%" stopColor="#fef08a" stopOpacity={0.6 * powerRatio} />
            <stop offset="55%" stopColor="#ffffff" stopOpacity={0.25 * powerRatio} />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>

          <filter id="solarLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <filter id="electricityGlow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ── 1. The Sun & Solar Radiance Flare (Positioned Above the Panel in Top Right Sky) ── */}
        {sunOpacity > 0 && (
          <g style={{ opacity: sunOpacity }}>
            {/* Outer Radiant Flare */}
            <circle
              cx="505"
              cy={sunY}
              r={sunRadius * 2.2}
              fill="url(#sunFlareGrad)"
              style={{
                transformOrigin: `505px ${sunY}px`,
                animation: "scada-sun-pulse 3s infinite ease-in-out",
              }}
            />
            {/* Sun Core Disc */}
            <circle
              cx="505"
              cy={sunY}
              r={sunRadius}
              fill="#fef08a"
              stroke="#f59e0b"
              strokeWidth="2"
              filter="url(#solarLedGlow)"
            />
            {/* Downward Radiating Solar Rays shining onto panel cells */}
            <line
              x1="490"
              y1={sunY + sunRadius}
              x2="410"
              y2="40"
              stroke="#fde047"
              strokeWidth="1.8"
              strokeDasharray="4 4"
              opacity="0.65"
            />
            <line
              x1="505"
              y1={sunY + sunRadius + 2}
              x2="465"
              y2="45"
              stroke="#fde047"
              strokeWidth="1.8"
              strokeDasharray="4 4"
              opacity="0.65"
            />
            <line
              x1="520"
              y1={sunY + sunRadius}
              x2="515"
              y2="40"
              stroke="#fde047"
              strokeWidth="1.8"
              strokeDasharray="4 4"
              opacity="0.65"
            />
          </g>
        )}

        {/* ── 2. Sunlight Sheen Sweep across Photovoltaic Cell Grid ── */}
        {powerRatio > 0 && (
          <g clipPath="url(#solarCellsClip)">
            <rect
              x="-200"
              y="0"
              width="900"
              height="240"
              fill="url(#solarSheenGrad)"
              style={{
                animation: "scada-solar-sweep 2.8s ease-in-out infinite",
              }}
            />
          </g>
        )}

        {/* ── 3. Electricity Flow Lines Between Solar Panel Strings ── */}
        {powerRatio > 0 && (
          <g clipPath="url(#solarCellsClip)" filter="url(#electricityGlow)">
            {/* String 1 Busbar Current */}
            <path
              d="M 125 15 L 90 200"
              stroke="#00d4ff"
              strokeWidth="4"
              strokeDasharray="12 6"
              strokeLinecap="round"
              style={{ animation: `scada-flow-forward ${currentDuration} linear infinite` }}
            />
            {/* String 2 Busbar Current */}
            <path
              d="M 235 15 L 210 200"
              stroke="#ffe600"
              strokeWidth="4.5"
              strokeDasharray="12 6"
              strokeLinecap="round"
              style={{ animation: `scada-flow-forward ${currentDuration} linear infinite 0.2s` }}
            />
            {/* String 3 Busbar Current */}
            <path
              d="M 350 15 L 340 200"
              stroke="#ffe600"
              strokeWidth="4.5"
              strokeDasharray="12 6"
              strokeLinecap="round"
              style={{ animation: `scada-flow-forward ${currentDuration} linear infinite 0.4s` }}
            />
            {/* String 4 Busbar Current */}
            <path
              d="M 460 15 L 465 200"
              stroke="#00d4ff"
              strokeWidth="4"
              strokeDasharray="12 6"
              strokeLinecap="round"
              style={{ animation: `scada-flow-forward ${currentDuration} linear infinite 0.6s` }}
            />
          </g>
        )}

        {/* ── Center Base Mounting Status LED ── */}
        <circle
          cx="292"
          cy="236"
          r="4.5"
          fill={dotColor}
          stroke="#ffffff"
          strokeWidth="1.2"
          filter={isRunning ? "url(#solarLedGlow)" : "none"}
          style={{
            animation: isRunning ? "scada-led-blink 2s infinite ease-in-out" : "none",
          }}
        />
      </svg>
    </div>
  );
};

RealisticSolarPanel.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default RealisticSolarPanel;
